import {
	Card,
	CardActionArea,
	CardMedia,
	Collapse,
	Grid,
	IconButton,
	Menu,
	MenuItem,
	Tooltip,
	Typography,
	useTheme,
} from "@mui/material";
import { Box } from "@mui/system";
import { useState } from "react";
import {
	AddOutlined,
	AssignmentOutlined,
	AttachFileOutlined,
	CloseOutlined,
	DeviceHubOutlined,
	ExpandLess,
	ExpandMore,
	MoreVertOutlined,
} from "@mui/icons-material";
import GrupoChip from "../../../../components/grupoEconomico/GrupoChip";
import useStyles from "./styles";
import labelCircuito from "./labelCircuito";

export default function CardSelection(params) {
	const {
		color = "grey",
		selected = false,
		idCliente,
		service,
		onClick,
		status,
		form,
		selectCliente,
		setSelectCliente,
		isConfigured = false,
		ownerNome,
		// Subitens de circuito do serviço (Task 6): cada item é { circuit, isConfigured }
		circuits = [],
		selectedCircuitKey,
		onSelectCircuit,
		editMode = false,
		onRemoveCircuit,
		// Expandir/recolher os subitens de circuito (adaptação visual do padrão 9.0-clientesSwap) — só usado quando o serviço tem circuitos
		hasChildren = false,
		isExpanded = false,
		onToggleExpand,
		// Menu de ações do serviço: hoje só "Adicionar circuito", sempre acessível mesmo fora de edição
		onAdicionarCircuito,
	} = params;

	const theme = useTheme();
	const styles = useStyles(theme);
	// Âncora do menu de ações do serviço (null = fechado)
	const [menuAnchor, setMenuAnchor] = useState(null);
	const waitingReturn = [
		"Aguardando Instalação",
		"Aguardando Assinatura de Contrato",
		"Aguardando Configuração",
		"Aguardando Liberação de TI",
		"Aguardando Migração",
		"Aguardando Viabilidade",
		"Análise de Pendências Cadastrais",
		"Em análise cadastral",
		"Aguardando Instalação (Pré-Pago)",
		"Pré-cadastro",
	];
	const enabledService = [
		"Serviço Habilitado",
		"Agendado para Instalação",
		"Ativo",
		"Normal",
		"ATIVO",
	];

	return (
		<Grid sx={{ mb: 0.5 }}>
			<Card variant="outlined">
				<Box
					data-active={selected ? "" : undefined}
					sx={{
						...styles.cardHeaderRow,
						border: selected
							? ` 2px solid ${
									waitingReturn.includes(status)
										? theme.palette.colors.orange.light
										: enabledService.includes(status)
											? theme.palette.colors.green.light
											: theme.palette.colors.red.light
								}`
							: "none",
						"&::before": {
							content: '""',
							position: "absolute",
							left: 0,
							top: 0,
							width: "5px",
							height: "100%",
							...(waitingReturn.includes(status)
								? {
										bgcolor: theme.palette.colors.orange.light,
										animation: "pulse 1.5s infinite",
										boxShadow: `0 0 10px ${theme.palette.colors.orange.light}`,
									}
								: enabledService.includes(status)
									? {
											bgcolor: theme.palette.colors.green.light,
										}
									: {
											bgcolor: theme.palette.colors.red.light,
										}),
						},
						"&[data-active]": {
							backgroundColor: "action.selected",
							"&:hover": {
								backgroundColor: "action.selectedHover",
							},
						},
					}}
				>
					{/* Botão de expandir/recolher os subitens de circuito, só quando o serviço tiver algum */}
					{hasChildren && (
						<CardMedia sx={styles.circuitExpandMedia}>
							<CardActionArea
								onClick={onToggleExpand}
								onMouseDown={(e) => e.stopPropagation()}
								size="small"
								sx={styles.circuitExpandAction}
							>
								{isExpanded ? <ExpandLess /> : <ExpandMore />}
							</CardActionArea>
						</CardMedia>
					)}
					<CardActionArea
						data-active={selected ? "" : undefined}
						sx={{ ...styles.cardMainAction, paddingX: hasChildren ? 0 : 1.5 }}
						onClick={onClick}
					>
						<Box sx={{ p: 0.5 }}>
							<Typography
								sx={{ fontWeight: "bold", fontSize: "14px" }}
								component="span"
							>{` ${idCliente} - ${service}`}</Typography>
							{ownerNome && (
								<Box sx={{ mt: 0.5 }}>
									<GrupoChip label={ownerNome} variant="owner" />
								</Box>
							)}
							<Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
								<Typography sx={{ fontSize: "12px", fontWeight: "bold" }}>
									Status: {status}
								</Typography>
								{isConfigured && (
									<Box
										sx={{
											display: "inline-flex",
											alignItems: "center",
											px: 1,
											py: 0.25,
											borderRadius: 1,
											fontSize: "0.65rem",
											fontWeight: "bold",
											bgcolor: "success.light",
											color: "success.contrastText",
										}}
									>
										CONFIGURADO
									</Box>
								)}
							</Box>
						</Box>
					</CardActionArea>
					{/* Menu de ações do serviço, sempre acessível independente de modo edição */}
					<CardMedia sx={styles.serviceMenuMedia}>
						<IconButton
							size="small"
							sx={styles.serviceMenuButton}
							onClick={(event) => {
								event.stopPropagation();
								setMenuAnchor(event.currentTarget);
							}}
							onMouseDown={(event) => event.stopPropagation()}
						>
							<MoreVertOutlined fontSize="small" />
						</IconButton>
						<Menu
							anchorEl={menuAnchor}
							open={Boolean(menuAnchor)}
							onClose={() => setMenuAnchor(null)}
							onClick={(event) => event.stopPropagation()}
						>
							<MenuItem
								onClick={() => {
									setMenuAnchor(null);
									onAdicionarCircuito?.();
								}}
							>
								<AddOutlined fontSize="small" sx={{ mr: 1 }} />
								Adicionar circuito
							</MenuItem>
						</Menu>
					</CardMedia>
				</Box>

				{/* Subitens de circuito do serviço (Task 6), agora recolhíveis (padrão visual do 9.0-clientesSwap) */}
				{hasChildren && (
					<Collapse in={isExpanded} timeout="auto" unmountOnExit>
						<Box sx={styles.circuitList}>
							{circuits.map((item, index) => {
								const circuitKey = item?.circuit?.id || item?.circuit?.tempId;
								const isSelectedCircuit = selected && circuitKey === selectedCircuitKey;
								const podeRemover =
									selected && editMode && circuits.length > 1;
								const anexosCount = item?.anexosCount || 0;
								const isLast = index === circuits.length - 1;

								return (
									<Box
										key={circuitKey || index}
										sx={{ ...styles.circuitConnectorWrap, pb: isLast ? 0 : 0.75 }}
									>
										{/* Linha conectora pai/filho: espinha vertical + espora horizontal até o card (padrão visual anterior, mantido a pedido).
										pb aqui (não gap no container) é o que mantém a espinha vertical contínua entre cards — ver styles.js */}
										<Box
											sx={{
												...styles.circuitConnectorVertical,
												top: index === 0 ? "0px" : 0,
												bottom: isLast ? "50%" : 0,
											}}
										/>
										<Box sx={styles.circuitConnectorHorizontal} />
										<Box
											sx={{
												...styles.circuitItem,
												...(isSelectedCircuit ? styles.circuitItemSelected : {}),
											}}
											onClick={() => onSelectCircuit?.(item?.circuit, index)}
										>
											<Box sx={styles.circuitItemLabel}>
												<Typography component="span" sx={{ fontSize: "0.75rem", fontWeight: 600 }}>
													{labelCircuito(item?.circuit, index)}
												</Typography>
											</Box>
											<Box sx={styles.circuitItemIcons}>
												{/* Tooltips seguem o padrão de fluxoAtendimentoEtapaComponent (placement="top" + arrow, ícone
												embrulhado num Box em vez de sozinho — alvo de hover mais estável). Nenhuma cor de tema é
												setada dentro do title: o balão do MUI Tooltip é sempre fundo escuro/texto branco, igual nos
												dois modos — se algum dia esse title virar conteúdo rico (JSX), a cor do texto deve ficar
												implícita (herdar o branco do balão) ou usar common.white explicitamente, nunca text.primary/
												text.secondary (que inverte com o tema e pode ficar ilegível sobre o balão sempre escuro) */}
												{/* Formulário: mesmo critério do chip CONFIGURADO do serviço pai (isCircuitoConfigurado) */}
												<Tooltip
													placement="top"
													arrow
													title={
														item?.isConfigured
															? "Formulário preenchido"
															: "Este circuito ainda não possui formulário preenchido"
													}
												>
													<Box sx={{ display: "flex", alignItems: "center" }}>
														<AssignmentOutlined
															sx={{
																fontSize: 16,
																color: item?.isConfigured
																	? theme.palette.colors.green.medium
																	: theme.palette.text.disabled,
															}}
														/>
													</Box>
												</Tooltip>
												{/* Anexos: contador, igual ao padrão de 9.0-clientesSwap */}
												<Tooltip
													placement="top"
													arrow
													title={
														anexosCount > 0
															? `${anexosCount} anexo${anexosCount > 1 ? "s" : ""}`
															: "Este circuito não possui anexo cadastrado"
													}
												>
													<Box
														sx={{
															display: "flex",
															alignItems: "center",
															gap: 0.25,
															color: anexosCount > 0
																? theme.palette.colors.green.medium
																: theme.palette.text.disabled,
														}}
													>
														<AttachFileOutlined sx={{ fontSize: 16 }} />
														<Typography sx={{ fontSize: "11px", fontWeight: 600, minWidth: 8 }}>
															{anexosCount}
														</Typography>
													</Box>
												</Tooltip>
												{/* Diagrama de equipamentos: só presença/ausência, igual ao ícone de mapa (KMZ) do 9.0-clientesSwap */}
												<Tooltip
													placement="top"
													arrow
													title={
														item?.temDiagrama
															? "Diagrama de equipamentos cadastrado"
															: "Este circuito não possui diagrama cadastrado"
													}
												>
													<Box sx={{ display: "flex", alignItems: "center" }}>
														<DeviceHubOutlined
															sx={{
																fontSize: 16,
																color: item?.temDiagrama
																	? theme.palette.colors.blue.light
																	: theme.palette.text.disabled,
															}}
														/>
													</Box>
												</Tooltip>
												{podeRemover && (
													<IconButton
														size="small"
														sx={styles.circuitItemClose}
														onClick={(event) => {
															event.stopPropagation();
															onRemoveCircuit?.(index);
														}}
													>
														<CloseOutlined sx={{ fontSize: 14 }} />
													</IconButton>
												)}
											</Box>
										</Box>
									</Box>
								);
							})}
						</Box>
					</Collapse>
				)}
			</Card>
		</Grid>
	);
}
